import { useEffect } from 'react';

export interface SEOMetadata {
  title: string;
  description: string;
  keywords?: string[];
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
  noindex?: boolean;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
}

const DEFAULT_OG_IMAGE = '/images/Social Sharing.png';
const DEFAULT_SITE_NAME = 'Simcarlo';
const DEFAULT_TWITTER_HANDLE = '@simcarlo'; // Update with actual Twitter handle if available

const getBaseUrl = (): string => {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
};

const truncate = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

export const useSEO = (metadata: SEOMetadata) => {
  useEffect(() => {
    const baseUrl = getBaseUrl();
    const fullTitle = metadata.title.includes(DEFAULT_SITE_NAME)
      ? metadata.title
      : `${metadata.title} | ${DEFAULT_SITE_NAME}`;
    
    // Ensure title is within optimal length (50-60 characters)
    const optimizedTitle = truncate(fullTitle, 60);
    
    // Ensure description is within optimal length (150-160 characters)
    const optimizedDescription = truncate(metadata.description, 160);
    
    // Set document title
    document.title = optimizedTitle;
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', optimizedDescription);
    
    // Update or create keywords meta tag
    if (metadata.keywords && metadata.keywords.length > 0) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', metadata.keywords.join(', '));
    }
    
    // Robots meta tag
    if (metadata.noindex) {
      let metaRobots = document.querySelector('meta[name="robots"]');
      if (!metaRobots) {
        metaRobots = document.createElement('meta');
        metaRobots.setAttribute('name', 'robots');
        document.head.appendChild(metaRobots);
      }
      metaRobots.setAttribute('content', 'noindex, nofollow');
    } else {
      // Remove noindex if it exists
      const metaRobots = document.querySelector('meta[name="robots"]');
      if (metaRobots && metaRobots.getAttribute('content')?.includes('noindex')) {
        metaRobots.remove();
      }
    }
    
    // Canonical URL
    const canonicalUrl = metadata.canonicalUrl || `${baseUrl}${window.location.pathname}`;
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', canonicalUrl);
    
    // Open Graph tags
    const ogImage = metadata.ogImage || `${baseUrl}${DEFAULT_OG_IMAGE}`;
    const ogUrl = canonicalUrl;
    const ogType = metadata.ogType || 'website';
    
    const setOrCreateMeta = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    setOrCreateMeta('og:title', optimizedTitle);
    setOrCreateMeta('og:description', optimizedDescription);
    setOrCreateMeta('og:image', ogImage);
    setOrCreateMeta('og:url', ogUrl);
    setOrCreateMeta('og:type', ogType);
    setOrCreateMeta('og:site_name', DEFAULT_SITE_NAME);
    
    // Twitter Card tags
    const twitterCard = metadata.twitterCard || 'summary_large_image';
    const setOrCreateTwitterMeta = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    setOrCreateTwitterMeta('twitter:card', twitterCard);
    setOrCreateTwitterMeta('twitter:title', optimizedTitle);
    setOrCreateTwitterMeta('twitter:description', optimizedDescription);
    setOrCreateTwitterMeta('twitter:image', ogImage);
    setOrCreateTwitterMeta('twitter:site', DEFAULT_TWITTER_HANDLE);
    setOrCreateTwitterMeta('twitter:creator', DEFAULT_TWITTER_HANDLE);
    
    // Cleanup function (optional, but good practice)
    return () => {
      // Note: We don't remove meta tags on cleanup as they should persist
      // until the next page navigation updates them
    };
  }, [metadata]);
};

